import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, Skeleton, Stack } from 'components/base';
import NoResponseData from 'pages/Forms/FormResults/FormResultDetails/Response/modal/NoResponseData';
import FormFieldPreview from 'pages/Forms/RegisterForm/CustomFormBuilder/Header/Preview/components/FormFieldPreview';
import useCustomForm from 'pages/Forms/context/useCustomForm';
import { FormElement, LinkGenerated, Setting } from 'pages/Forms/types/form';
import { GeoLocation } from 'pages/Forms/RegisterForm/CustomFormBuilder/components/Geolocation';
import { extractElementIDsWithNonEmptyPrefilledAnswer } from 'utils/module/Forms';
import { usePreventReload } from 'utils/PreventReloadHandler/usePreventHandlerNew';
import { Header } from './Header';
import { useFetchFormDataById } from '../../api/queries';
import { useQRStore } from '../../store/useQRStore';
import { useAddResponse } from '../../hooks/useAddResponse';

type CustomFormState = [
  { currentLocation: Location | null; formFields: FormElement[] },
  {
    setAnswer: (id: string, value: string) => void;
    setFormFields: (fields: FormElement[]) => void;
  },
];

export const AddResponse: React.FC = () => {
  const [{ currentLocation, formFields }, { setAnswer, setFormFields }] =
    useCustomForm() as unknown as CustomFormState;
  const { isDirty, dataFromURL, setIsDirty } = useQRStore();
  const { id } = useParams();

  const [isPrefillEditable, setIsPrefillEditable] = useState(true);
  const [prefilledElementIDs, setPrefilledElementIDs] = useState<string[]>([]);
  const { PromptComponent, disablePrompt } = usePreventReload(isDirty);

  const { data: formDataById, isPending: isLoadingFormDataById } =
    useFetchFormDataById(id as string);

  const isGeolocationEnabled = formDataById?.collect_geolocation;

  const { handleFormAnswerEntrySubmit, formFieldErrors, isSavingResponse } =
    useAddResponse({
      formID: formDataById?.id ?? '',
      form_title: formDataById?.form_title ?? '',
      formDescription: formDataById?.form_description ?? '',
      formFields,
      setting: {
        ...formDataById?.setting,
        location: isGeolocationEnabled ? currentLocation : null,
      } as unknown as Setting,
      disablePrompt,
    });

  useEffect(() => {
    if (formDataById) {
      const { linkID, hasPrefill } = dataFromURL ?? {};

      if (hasPrefill) {
        const linkData = formDataById?.share_form?.find(
          (item: LinkGenerated) => item.id === linkID,
        );
        setIsPrefillEditable(linkData?.is_prefill_editable as boolean);
        const prefillData = linkData?.prefill;
        if (prefillData) {
          const elementIDDswithNonEmptyPrefilledAnswer =
            extractElementIDsWithNonEmptyPrefilledAnswer(prefillData);
          setPrefilledElementIDs(elementIDDswithNonEmptyPrefilledAnswer);
          setFormFields(prefillData);
        }
      } else {
        setFormFields(formDataById.form.elements);
      }
    }
  }, [formDataById]);

  return (
    <>
      {PromptComponent}
      {isLoadingFormDataById ? (
        <Stack height="70%" width="80%" spacing={2}>
          {Array.from({ length: 10 }).map((_, index) => (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              key={index}
            />
          ))}
        </Stack>
      ) : (
        <>
          <Header
            form_title={formDataById?.form_title ?? ''}
            form_description={formDataById?.form_description ?? ''}
            handleSubmit={handleFormAnswerEntrySubmit}
            isLoading={isSavingResponse}
          />
          {isGeolocationEnabled && (
            <Alert
              variant="outlined"
              severity="info"
              color="warning"
              sx={{ width: '80%' }}
            >
              Geolocation tracking is enabled in this form. This form will
              capture approximate location from where the form is submitted.
            </Alert>
          )}

          {formDataById?.form?.elements?.length ? (
            <Box width="80%" height="70%" sx={{ overflowY: 'auto' }}>
              {formDataById?.form?.elements?.map((element) => {
                const fieldAnswerData = Array.isArray(formFields)
                  ? formFields.find((formField) => formField.id === element?.id)
                  : null;
                if (!fieldAnswerData) return null;

                const { answer } = fieldAnswerData as { answer: string };
                const handleAnswerChange = (
                  e: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  setAnswer(element?.id, e.target.value);
                  setIsDirty(true);
                };
                return (
                  <FormFieldPreview
                    key={element?.id}
                    element={element}
                    answer={answer}
                    handleAnswerChange={handleAnswerChange}
                    error={formFieldErrors[element?.id]}
                    field={fieldAnswerData}
                    isPreview={false}
                    isPrefillEditable={isPrefillEditable}
                    prefilledElementIDs={prefilledElementIDs}
                  />
                );
              })}
              {isGeolocationEnabled && <GeoLocation />}
            </Box>
          ) : (
            <NoResponseData subtitle={''} />
          )}
        </>
      )}
    </>
  );
};
